/**
 * ActEditor.tsx — UI for creating a completion act (protokół odbioru, DOC-02).
 *
 * Features:
 * - Lists estimate items with ordered qty, previously closed (closedByItem), and remaining.
 * - Per-item closedQty numeric input with per-item remaining guard.
 * - On submit: validateActClosures → buildActData → persist via appendAct → download PDF.
 * - Over-closure guard runs BEFORE persistence and PDF (PATTERNS constraint 6, T-07-06).
 * - No financial math in this component — all computation in buildActData/computeEstimateTotals.
 * - Gate drives isDraft: isContractsLegallyApproved() → approved → not draft.
 * - All strings via t() (4-locale keys from plan 07-01).
 *
 * CLAUDE.md rule 8: no roundTo/toFixed in JSX — values come from buildActData.
 * CLAUDE.md rule 13: no hardcoded strings — t() with fallback.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/stores/useProjectStore';
import { validateActClosures, buildActData } from '@/entities/document/lib/buildActData';
import { isContractsLegallyApproved } from '../lib/isContractsEnabled';
import type { LineResult } from '@/entities/estimate/lib/computeTotals';
import type { ActClosureInput } from '@/entities/document/lib/buildActData';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActEditorProps {
  /** Estimate lines from computeEstimateTotals — provides finalQty + priceLabor per item */
  estimateLines: LineResult[];
  /** VAT rate (decimal, e.g. 0.23) — passed to buildActData */
  vatRate: number;
  /** Currency string for display (e.g. 'PLN') */
  currency?: string;
  /** Called when the act has been created and PDF downloaded — parent can close modal etc. */
  onActCreated?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActEditor({ estimateLines, vatRate, currency = 'PLN', onActCreated }: ActEditorProps) {
  const { t } = useTranslation();
  const { actState, appendAct } = useProjectStore();

  // Act metadata fields
  const [actNumber, setActNumber] = useState('');
  const [actDate, setActDate] = useState(new Date().toISOString().slice(0, 10));
  const [actType, setActType] = useState<'partial' | 'final'>('partial');
  const [contractNumber, setContractNumber] = useState('');

  // Per-item closedQty inputs — keyed by itemId
  const [closedQtyMap, setClosedQtyMap] = useState<Record<string, string>>({});
  // Per-item validation error messages (from validateActClosures)
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  // Global generating state
  const [generating, setGenerating] = useState(false);

  // Helper: previously closed qty for an item
  const getPreviouslyClosed = useCallback((itemId: string) => {
    return actState.closedByItem[itemId] ?? 0;
  }, [actState.closedByItem]);

  // Helper: remaining qty for an item
  const getRemaining = useCallback((line: LineResult) => {
    const prev = getPreviouslyClosed(line.itemId);
    return Math.max(0, line.finalQty - prev);
  }, [getPreviouslyClosed]);

  // On closedQty input change — update map and run per-item validation
  const handleQtyChange = useCallback((itemId: string, value: string) => {
    setClosedQtyMap((prev) => ({ ...prev, [itemId]: value }));

    const parsed = parseFloat(value);
    if (value !== '' && (isNaN(parsed) || parsed < 0)) {
      setItemErrors((prev) => ({
        ...prev,
        [itemId]: t('contract.act_closes_too_much', 'Closed quantity exceeds remaining quantity'),
      }));
      return;
    }

    // Run guard on single item with current actState
    if (value !== '' && !isNaN(parsed)) {
      const line = estimateLines.find((l) => l.itemId === itemId);
      if (line) {
        const result = validateActClosures(
          [{ itemId, closedQty: parsed }],
          actState,
          [line],
        );
        if (!result.valid) {
          setItemErrors((prev) => ({
            ...prev,
            [itemId]: t('contract.act_closes_too_much', 'Closed quantity exceeds remaining quantity'),
          }));
        } else {
          setItemErrors((prev) => {
            const next = { ...prev };
            delete next[itemId];
            return next;
          });
        }
      }
    } else {
      setItemErrors((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
    }
  }, [actState, estimateLines, t]);

  // Build closures from the input map (only items with a non-empty positive value)
  const buildClosures = useCallback((): ActClosureInput[] => {
    const closures: ActClosureInput[] = [];
    for (const line of estimateLines) {
      const raw = closedQtyMap[line.itemId];
      if (!raw || raw === '') continue;
      const qty = parseFloat(raw);
      if (isNaN(qty) || qty <= 0) continue;
      closures.push({ itemId: line.itemId, closedQty: qty });
    }
    return closures;
  }, [closedQtyMap, estimateLines]);

  // Whether the form is valid for submission
  const hasErrors = Object.keys(itemErrors).length > 0;
  const closures = buildClosures();
  const canCreate = !hasErrors && closures.length > 0 && actNumber.trim() !== '' && !generating;

  // Submit: validate → build → persist → PDF download
  const handleCreate = useCallback(async () => {
    if (!canCreate) return;
    setGenerating(true);

    try {
      // Final validation (guard runs before any mutation — T-07-06, PATTERNS constraint 6)
      const validation = validateActClosures(closures, actState, estimateLines);
      if (!validation.valid) {
        const newErrors: Record<string, string> = {};
        for (const err of validation.errors) {
          newErrors[err.itemId] = t('contract.act_closes_too_much', 'Closed quantity exceeds remaining quantity');
        }
        setItemErrors(newErrors);
        return;
      }

      // Build act + updated state (pure, throws if guard fails — belt-and-suspenders)
      const { act, updatedActState } = buildActData({
        existingActState: actState,
        closures,
        estimateLines,
        vatRate,
        actNumber: actNumber.trim(),
        actDate,
        actType,
        contractNumber: contractNumber.trim(),
      });

      // Persist BEFORE PDF download (CLAUDE.md rule 11: data safety)
      appendAct(act, updatedActState);

      // Get the previously-closed map BEFORE this act (for the PDF's "wcześniej zamknięte" column)
      const previouslyClosedByItem = { ...actState.closedByItem };

      // Gate check
      const approved = isContractsLegallyApproved();

      // Dynamic import — font barrel stays out of main bundle (lazy pattern)
      const { generateProtokolPDF } = await import('@/entities/document/lib/pdfActGenerator');
      // WR-05 fix: read real project meta so the protokół header/signature block shows
      // contractor and client names instead of being blank.
      const { meta: projectMeta } = useProjectStore.getState();
      await generateProtokolPDF(
        act,
        estimateLines,
        previouslyClosedByItem,
        {
          contractorName: projectMeta.contractorName,
          clientName: projectMeta.clientName,
          projectName: projectMeta.clientAddress,
        },
        approved,
        currency,
      );

      // Reset form on success
      setClosedQtyMap({});
      setActNumber('');
      setContractNumber('');
      setItemErrors({});
      onActCreated?.();
    } catch (err) {
      console.error(t('contract.export_error', 'Document generation error'), err);
    } finally {
      setGenerating(false);
    }
  }, [
    canCreate,
    closures,
    actState,
    estimateLines,
    vatRate,
    actNumber,
    actDate,
    actType,
    contractNumber,
    appendAct,
    currency,
    onActCreated,
    t,
  ]);

  // Items where there is still remaining volume to close
  const closableLines = estimateLines.filter((l) => getRemaining(l) > 0);

  return (
    <div className="space-y-4 p-4">
      <h2 className="text-lg font-bold text-slate-900">
        {t('contract.act_editor_title', 'Completion act')}
      </h2>

      {/* Act metadata */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('contract.act_number', 'Act number')}
          </label>
          <input
            type="text"
            value={actNumber}
            onChange={(e) => setActNumber(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
            placeholder="P-001"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('contract.act_date', 'Date')}
          </label>
          <input
            type="date"
            value={actDate}
            onChange={(e) => setActDate(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('contract.act_type', 'Act type')}
          </label>
          <select
            value={actType}
            onChange={(e) => setActType(e.target.value as 'partial' | 'final')}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
          >
            <option value="partial">{t('contract.act_type_partial', 'Partial')}</option>
            <option value="final">{t('contract.act_type_final', 'Final')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            {t('contract.contract_number', 'Contract number')}
          </label>
          <input
            type="text"
            value={contractNumber}
            onChange={(e) => setContractNumber(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none"
            placeholder="K/2026/001"
          />
        </div>
      </div>

      {/* Closed items table */}
      {closableLines.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">
          {t('contract.act_no_items', 'All estimate items have been fully closed.')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="px-2 py-2 text-left font-medium">
                  {t('contract.act_col_item', 'Item')}
                </th>
                <th className="px-2 py-2 text-right font-medium">
                  {t('contract.act_col_ordered', 'Ordered')}
                </th>
                <th className="px-2 py-2 text-right font-medium">
                  {t('contract.act_col_closed', 'Previously closed')}
                </th>
                <th className="px-2 py-2 text-right font-medium">
                  {t('contract.act_remaining', 'Remaining')}
                </th>
                <th className="px-2 py-2 text-right font-medium w-28">
                  {t('contract.act_closed_qty', 'Accepted quantity')}
                </th>
              </tr>
            </thead>
            <tbody>
              {closableLines.map((line, idx) => {
                const prev = getPreviouslyClosed(line.itemId);
                const remaining = getRemaining(line);
                const error = itemErrors[line.itemId];
                const isEven = idx % 2 === 0;

                return (
                  <tr
                    key={line.itemId}
                    className={`border-b border-slate-200 ${isEven ? 'bg-slate-50' : 'bg-white'}`}
                  >
                    <td className="px-2 py-2 text-slate-700">{line.itemId}</td>
                    <td className="px-2 py-2 text-right text-slate-600">{line.finalQty.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right text-slate-500">{prev.toFixed(2)}</td>
                    <td className={`px-2 py-2 text-right font-medium ${remaining > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {remaining.toFixed(2)}
                    </td>
                    <td className="px-2 py-2">
                      <div>
                        <input
                          type="number"
                          min={0}
                          max={remaining}
                          step="any"
                          value={closedQtyMap[line.itemId] ?? ''}
                          onChange={(e) => handleQtyChange(line.itemId, e.target.value)}
                          className={`w-full rounded border px-2 py-1 text-right text-xs focus:outline-none ${
                            error
                              ? 'border-red-400 bg-red-50 focus:border-red-500'
                              : 'border-slate-300 focus:border-emerald-500'
                          }`}
                          placeholder="0"
                        />
                        {error && (
                          <p className="text-red-500 text-xs mt-0.5 leading-tight">{error}</p>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Action button */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleCreate}
          disabled={!canCreate}
          aria-busy={generating}
          className={`rounded px-4 py-2 text-sm font-medium transition-colors ${
            canCreate
              ? 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          {generating
            ? t('contract.generating', 'Generating...')
            : t('contract.act_create', 'Create act')}
        </button>
      </div>
    </div>
  );
}
